import './ffsCalc/ffsCalcCardStyles.css'

import { Select, Tabs, TabsProps, Typography } from 'antd'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { IndicationDetailCardProps } from 'features/indicationTriage/useIndicationDetailCards'
import React, { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoComponentModel } from 'types'

import { FfsLevel, FfsPart } from './ffsCalc/ffsResultPublish/ffsTypes'
import { FfsSharedValueProvider } from './ffsCalc/FfsSharedValues'
import { FfsStrategyProvider } from './ffsCalc/ffsStrategyContext/FfsStrategyContext'
import { FfsPartFourCalc } from './ffsCalc/part4/FfsPartFourCalc'
import { FfsPartFiveLevelOneCalc } from './ffsCalc/part5LevelOne/FfsLevelOneCalc'
import { FfsPartFiveLevelTwoCalc } from './ffsCalc/part5LevelTwo/FfsLevelTwoCalc'

const { Title } = Typography

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const PartSelectorContainer = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
`

interface FfsTabContentConfig {
  displayName: string
  levels: {
    key: string
    level: FfsLevel
    label: string
    component: FC<FfsCalcCardContentProps>
  }[]
}

const FFS_TAB_COMPONENT_REGISTRY: Record<FfsPart, FfsTabContentConfig> = {
  [FfsPart.PART_4]: {
    displayName: 'Part 4: General Corrosion',
    levels: [
      {
        key: 'ffs-part4-level-1',
        level: '1' as FfsLevel,
        label: 'Level 1',
        component: FfsPartFourCalc,
      },
      {
        key: 'ffs-part4-level-2',
        level: '2' as FfsLevel,
        label: 'Level 2',
        component: FfsPartFourCalc,
      },
    ],
  },
  [FfsPart.PART_5]: {
    displayName: 'Part 5: Local Metal Loss',
    levels: [
      {
        key: 'ffs-part5-level-1',
        level: '1' as FfsLevel,
        label: 'Level 1',
        component: FfsPartFiveLevelOneCalc,
      },
      {
        key: 'ffs-part5-level-2',
        level: '2' as FfsLevel,
        label: 'Level 2',
        component: FfsPartFiveLevelTwoCalc,
      },
    ],
  },
}

export interface FfsCalcCardContentProps {
  ffsLevel: FfsLevel
}

export const Width = '24rem'

export const CardView: FC<IndicationDetailCardProps> = () => {
  const {
    assetModel,
    relevantThicknessCDF: thicknessCdf,
    inspection,
  } = useIndicationData()
  const [selectedPart, setSelectedPart] = useState<FfsPart>(FfsPart.PART_4)

  const componentModel: GeckoComponentModel | undefined = useMemo(() => {
    const components = assetModel?.data.components
    const componentId = thicknessCdf?.tags.component
    if (!components || !componentId) {
      return undefined
    }

    return components.get(componentId) ?? undefined
  }, [assetModel?.data.components, thicknessCdf?.tags.component])

  const ffsCalcCardContentProps = useMemo(
    () => ({
      componentModel,
    }),
    [componentModel]
  )

  const tabItems = useMemo<TabsProps['items']>(() => {
    const partConfig: FfsTabContentConfig =
      FFS_TAB_COMPONENT_REGISTRY[selectedPart]

    return partConfig.levels.map((levelConfig) => {
      const CalcComponent: FC<FfsCalcCardContentProps> = levelConfig.component
      return {
        key: levelConfig.key,
        label: levelConfig.label,
        children: (
          <CalcComponent
            {...ffsCalcCardContentProps}
            ffsLevel={levelConfig.level}
          />
        ),
      }
    })
  }, [ffsCalcCardContentProps, selectedPart])

  return (
    <CardContainer>
      <Title level={3}>FFS Calculations</Title>
      <PartSelectorContainer>
        <Select
          value={selectedPart}
          onChange={setSelectedPart}
          style={{ width: '100%' }}
          options={Object.entries(FFS_TAB_COMPONENT_REGISTRY).map(
            ([part, config]) => ({
              value: part as FfsPart,
              label: config.displayName,
            })
          )}
        />
      </PartSelectorContainer>
      <FfsStrategyProvider
        componentModelType={componentModel?.type}
        ffsPart={selectedPart}>
        <FfsSharedValueProvider
          componentModel={componentModel}
          thicknessCdf={thicknessCdf}
          inspection={inspection}>
          <Tabs
            defaultActiveKey={tabItems![0].key}
            items={tabItems}
            animated={{ inkBar: true, tabPane: true }}
          />
        </FfsSharedValueProvider>
      </FfsStrategyProvider>
    </CardContainer>
  )
}
